import { createSignal, onMount, Show } from "solid-js";
import { BRAND } from "./brand.ts";

/**
 * What this is, said once.
 *
 * Everybody who arrives here arrives at a text field, which answers "what do I type" and never
 * answers "what is this". Shown on a first visit and never again, because an explanation you
 * cannot get past stops being an explanation and becomes a toll.
 */

const KEY = "dodobop.seen";

export function seenIntro(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    // A browser that will not remember should not be shown this every single time either.
    return true;
  }
}

function remember(): void {
  try {
    localStorage.setItem(KEY, "1");
  } catch {
    // Nothing to do, and nothing worth doing.
  }
}

export function Intro(props: { onDone: () => void }) {
  const [going, setGoing] = createSignal(false);

  const done = (): void => {
    setGoing(true);
    remember();
    window.setTimeout(props.onDone, 260);
  };

  onMount(() => {
    const key = (event: KeyboardEvent): void => {
      if (event.key === "Escape" || event.key === "Enter") done();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });

  return (
    <div class="intro" classList={{ "intro--going": going() }}>
      <div class="intro__middle">
        <p class="intro__over">{BRAND}</p>
        <h1 class="intro__what">
          Any song becomes
          <br />a game you can play
        </h1>

        <ol class="intro__steps">
          <li>
            <b>Paste a link.</b> A YouTube video, or any audio file.
          </li>
          <li>
            <b>It listens once</b> and writes the notes from what it hears.
          </li>
          <li>
            <b>Send it to anyone.</b> They play the same song, and you both get a score.
          </li>
        </ol>

        <button class="intro__go" onClick={done}>
          Show me
        </button>
        <p class="intro__small">Nothing is copied. The song stays where it already lives.</p>
      </div>
    </div>
  );
}

/** Wraps a page so the explanation happens before it, once, and then never appears again. */
export function WithIntro(props: { children: () => unknown }) {
  const [showing, setShowing] = createSignal(!seenIntro());
  return (
    <>
      <Show when={showing()}>
        <Intro onDone={() => setShowing(false)} />
      </Show>
      {props.children() as never}
    </>
  );
}
